import { CheckIcon, SearchIcon } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { trpc } from '../../lib/trpc';

const RECENT_STORAGE_KEY = 'budgie:recent-categories';
const MAX_RECENT = 5;

export type CategorySuggestion = { categoryId: string; label: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value?: string | null;
  onSelect: (categoryId: string, opts: { createRule: boolean }) => void;
  suggestions?: CategorySuggestion[];
  title?: string;
  showCreateRule?: boolean;
};

/** Categories picked most recently (newest first), for quick-pick chips */
export function readRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === 'string').slice(0, MAX_RECENT)
      : [];
  } catch {
    return [];
  }
}

export function rememberRecent(categoryId: string) {
  try {
    const next = [categoryId, ...readRecent().filter((id) => id !== categoryId)].slice(
      0,
      MAX_RECENT,
    );
    window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable (private mode, quota) — recent list is a convenience only.
  }
}

function isMobileViewport() {
  return typeof window !== 'undefined' && window.matchMedia?.('(max-width: 639px)').matches;
}

/**
 * Searchable category picker. Bottom sheet on mobile, centred dialog from `sm` up.
 * Selecting a category calls `onSelect` immediately and closes the picker.
 */
export function CategoryPickerSheet({
  open,
  onOpenChange,
  value,
  onSelect,
  suggestions,
  title = 'Välj kategori',
  showCreateRule = false,
}: Props) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={contentRef}
        className="flex h-[85dvh] flex-col gap-3 overflow-hidden sm:h-[min(70vh,640px)] sm:max-w-md"
        onOpenAutoFocus={(e) => {
          // Don't pop the on-screen keyboard over the list on phones.
          if (isMobileViewport()) {
            e.preventDefault();
            contentRef.current?.focus();
          }
        }}
      >
        <DialogHeader className="pr-10">
          <DialogTitle className="line-clamp-2 text-[15px] leading-snug font-semibold">
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Sök eller välj en kategori i listan.
          </DialogDescription>
        </DialogHeader>
        {/* Body is only mounted while open, so search/checkbox state resets each time. */}
        <PickerBody
          value={value ?? null}
          suggestions={suggestions}
          showCreateRule={showCreateRule}
          onPick={(categoryId, createRule) => {
            rememberRecent(categoryId);
            onSelect(categoryId, { createRule });
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function PickerBody({
  value,
  suggestions,
  showCreateRule,
  onPick,
}: {
  value: string | null;
  suggestions?: CategorySuggestion[] | undefined;
  showCreateRule: boolean;
  onPick: (categoryId: string, createRule: boolean) => void;
}) {
  const groupsQuery = trpc.category.listGroups.useQuery();
  const [search, setSearch] = useState('');
  const [createRule, setCreateRule] = useState(false);
  const [recentIds] = useState(readRecent);
  const searchId = useId();
  const ruleId = useId();

  const groups = groupsQuery.data ?? [];

  const categoryById = useMemo(() => {
    const map = new Map<string, { name: string; groupName: string }>();
    for (const g of groupsQuery.data ?? []) {
      for (const c of g.categories) map.set(c.id, { name: c.name, groupName: g.name });
    }
    return map;
  }, [groupsQuery.data]);

  const term = search.trim().toLocaleLowerCase('sv-SE');
  const filteredGroups = useMemo(() => {
    if (!term) return groups;
    return groups
      .map((g) => {
        const groupMatches = g.name.toLocaleLowerCase('sv-SE').includes(term);
        return {
          ...g,
          categories: groupMatches
            ? g.categories
            : g.categories.filter((c) => c.name.toLocaleLowerCase('sv-SE').includes(term)),
        };
      })
      .filter((g) => g.categories.length > 0);
  }, [groups, term]);

  const suggestionChips = (suggestions ?? []).filter((s) => categoryById.has(s.categoryId));
  const suggestionIds = new Set(suggestionChips.map((s) => s.categoryId));
  const recentChips = recentIds
    .filter((id) => categoryById.has(id) && !suggestionIds.has(id))
    .map((id) => ({ categoryId: id, label: categoryById.get(id)?.name ?? '' }));

  const pick = (categoryId: string) => onPick(categoryId, createRule);

  return (
    <>
      <div className="relative">
        <SearchIcon
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <label htmlFor={searchId} className="sr-only">
          Sök kategori
        </label>
        <Input
          id={searchId}
          type="search"
          inputMode="search"
          autoComplete="off"
          placeholder="Sök kategori…"
          className="h-11 rounded-xl pl-9 sm:h-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            const first = filteredGroups[0]?.categories[0];
            if (e.key === 'Enter' && term && first) {
              e.preventDefault();
              pick(first.id);
            }
          }}
        />
      </div>

      <div className="-mx-4 min-h-0 flex-1 overflow-y-auto overscroll-contain px-4">
        {!term && (suggestionChips.length > 0 || recentChips.length > 0) && (
          <div className="space-y-3 pb-3">
            <ChipRow heading="Förslag" chips={suggestionChips} value={value} onPick={pick} />
            <ChipRow heading="Senast använda" chips={recentChips} value={value} onPick={pick} />
          </div>
        )}

        {groupsQuery.isLoading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Laddar kategorier…</p>
        ) : groupsQuery.isError ? (
          <p className="py-6 text-center text-sm text-destructive">Kunde inte ladda kategorier.</p>
        ) : filteredGroups.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Inga kategorier matchar ”{search.trim()}”.
          </p>
        ) : (
          filteredGroups.map((group) => (
            <section key={group.id} aria-label={group.name} className="pb-2">
              <h3 className="sticky top-0 z-10 bg-popover px-3 py-1.5 text-xs font-medium text-muted-foreground">
                {group.name}
              </h3>
              <ul>
                {group.categories.map((cat) => {
                  const selected = cat.id === value;
                  return (
                    <li key={cat.id}>
                      <button
                        type="button"
                        aria-pressed={selected}
                        onClick={() => pick(cat.id)}
                        className={cn(
                          'flex min-h-12 w-full items-center justify-between gap-3 rounded-xl px-3 text-left text-base outline-none transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 sm:min-h-10 sm:text-sm',
                          selected && 'bg-primary/10 font-medium text-primary hover:bg-primary/15',
                        )}
                      >
                        <span className="min-w-0 truncate">{cat.name}</span>
                        {selected && <CheckIcon aria-hidden className="size-4 shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>

      {showCreateRule && (
        <div className="-mx-4 flex min-h-12 items-center gap-3 border-t border-border px-4 pt-3">
          <Checkbox
            id={ruleId}
            checked={createRule}
            onCheckedChange={(checked) => setCreateRule(checked === true)}
          />
          <label htmlFor={ruleId} className="text-sm">
            Kom ihåg för liknande (skapa regel)
          </label>
        </div>
      )}
    </>
  );
}

function ChipRow({
  heading,
  chips,
  value,
  onPick,
}: {
  heading: string;
  chips: CategorySuggestion[];
  value: string | null;
  onPick: (categoryId: string) => void;
}) {
  if (chips.length === 0) return null;
  return (
    <div>
      <p className="pb-1.5 text-xs font-medium text-muted-foreground">{heading}</p>
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <button
            key={chip.categoryId}
            type="button"
            onClick={() => onPick(chip.categoryId)}
            className={cn(
              'inline-flex h-11 max-w-full items-center rounded-full border border-border bg-card px-3.5 text-sm font-medium outline-none transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 sm:h-8',
              chip.categoryId === value && 'border-primary/25 bg-primary/10 text-primary',
            )}
          >
            <span className="truncate">{chip.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
